<template>
  <div
    v-for="eligiblePerson in eligibility"
    :key="eligiblePerson.personId.personId"
  >
    <WaitlistOfferChip
      v-if="
        classInstance.availability?.waitlistedEnrollAction ==
          UnifiedSignupTypeEnum.ENROLLED &&
        eligiblePerson.status == ClassEligibilityStatus.WAITLISTED_PENDING
      "
      :person-name="getName(eligiblePerson.personId.personId)"
      :expires-on="eligiblePerson.offerExpDatetime"
      :loading="loading"
      :is-purchase-hold="false"
      :price-cents="null"
      @claim="
        updateEnrollment(
          UnifiedSignupTypeEnum.ENROLLED,
          eligiblePerson.personId.personId,
        )
      "
    />
    <WaitlistOfferChip
      v-if="
        classInstance.availability?.waitlistedEnrollAction ==
          UnifiedSignupTypeEnum.PURCHASE_HOLD &&
        eligiblePerson.status == ClassEligibilityStatus.WAITLISTED_PENDING
      "
      :person-name="getName(eligiblePerson.personId.personId)"
      :expires-on="eligiblePerson.offerExpDatetime"
      :loading="loading"
      :is-purchase-hold="true"
      :price-cents="classInstance.availability?.priceCents ?? null"
      @claim="
        updateEnrollment(
          UnifiedSignupTypeEnum.PURCHASE_HOLD,
          eligiblePerson.personId.personId,
        )
      "
    />
    <v-chip
      v-if="eligiblePerson.status == ClassEligibilityStatus.ENROLLED"
      @click="
        updateEnrollment(
          UnifiedSignupTypeEnum.UNENROLLED,
          eligiblePerson.personId.personId,
        )
      "
      color="red"
      append-icon="mdi-book-minus"
    >
      <v-btn variant="text" :loading="loading"
        >Unenroll {{ getName(eligiblePerson.personId.personId) }}</v-btn
      ></v-chip
    >
    <v-chip
      v-else-if="
        eligiblePerson.status == ClassEligibilityStatus.WAITLISTED ||
        eligiblePerson.status == ClassEligibilityStatus.WAITLISTED_PENDING
      "
      @click="
        updateEnrollment(
          UnifiedSignupTypeEnum.UNENROLLED,
          eligiblePerson.personId.personId,
        )
      "
      color="red"
      append-icon="mdi-book-minus"
    >
      <v-btn variant="text" :loading="loading"
        >Remove {{ getName(eligiblePerson.personId.personId) }} from
        waitlist</v-btn
      ></v-chip
    >
    <PurchaseHoldChip
      v-else-if="eligiblePerson.status == ClassEligibilityStatus.PURCHASE_HOLD"
      :person-name="getName(eligiblePerson.personId.personId)"
      :expires-on="eligiblePerson.purchaseHoldExpiresOn"
      :loading="loading"
      :is-removal="true"
      @action="
        updateEnrollment(
          UnifiedSignupTypeEnum.UNENROLLED,
          eligiblePerson.personId.personId,
        )
      "
    />
    <v-tooltip
      v-else-if="
        eligiblePerson.status == ClassEligibilityStatus.INELIGIBLE ||
        eligiblePerson.status == ClassEligibilityStatus.UNKNOWN
      "
      :text="eligiblePerson.message ?? undefined"
    >
      <template #activator="{ props }">
        <v-chip v-bind="props">
          {{ getName(eligiblePerson.personId.personId) }}
        </v-chip>
      </template>
    </v-tooltip>

    <v-chip
      v-else-if="
        classInstance?.availability?.availabilityStatus ==
        ClassSignupAvailabilityStatus.AVAILABLE
      "
      color="green"
      append-icon="mdi-book-plus"
      @click="
        updateEnrollment(
          UnifiedSignupTypeEnum.ENROLLED,
          eligiblePerson.personId.personId,
        )
      "
      ><v-btn :loading="loading" variant="text"
        >Enroll {{ getName(eligiblePerson.personId.personId) }}</v-btn
      ></v-chip
    >

    <PurchaseHoldChip
      v-else-if="
        classInstance?.availability?.availabilityStatus ==
        ClassSignupAvailabilityStatus.PAID_AVAILABLE
      "
      :person-name="getName(eligiblePerson.personId.personId)"
      :expires-on="eligiblePerson.purchaseHoldExpiresOn"
      :loading="loading"
      :is-removal="false"
      :price-cents="classInstance.availability?.priceCents ?? null"
      @action="
        updateEnrollment(
          UnifiedSignupTypeEnum.PURCHASE_HOLD,
          eligiblePerson.personId.personId,
        )
      "
    />

    <v-chip
      v-else-if="
        classInstance?.availability?.availabilityStatus ==
        ClassSignupAvailabilityStatus.WAITLIST_AVAILABLE
      "
      color="warning"
      @click="
        updateEnrollment(
          UnifiedSignupTypeEnum.WAITLISTED,
          eligiblePerson.personId.personId,
        )
      "
      append-icon="mdi-book-clock-outline"
      ><v-btn :loading="loading" variant="text"
        >Waitlist {{ getName(eligiblePerson.personId.personId) }}</v-btn
      ></v-chip
    >
    <v-tooltip
      v-else-if="
        classInstance?.availability?.availabilityStatus ==
        ClassSignupAvailabilityStatus.NO_SIGNUP_UNAVAILABLE
      "
      text="This class does not require signups. Just arrive at the specified time."
    >
      <template #activator="{ props }">
        <v-chip v-bind="props">
          {{ getName(eligiblePerson.personId.personId) }}
        </v-chip>
      </template>
    </v-tooltip>
    <v-tooltip
      v-else-if="
        classInstance.availability?.availabilityStatus ==
        ClassSignupAvailabilityStatus.FUTURE_UNAVAILABLE
      "
      :text="getAvailableOnText(classInstance.availability!!)"
    >
      <template #activator="{ props }">
        <v-chip v-bind="props" append-icon="mdi-clock">
          {{ getName(eligiblePerson.personId.personId) }}
        </v-chip>
      </template>
    </v-tooltip>
    <v-tooltip
      v-else-if="
        classInstance.availability?.availabilityStatus ==
        ClassSignupAvailabilityStatus.STARTED_UNAVAILABLE
      "
      text="This class has already started"
    >
      <template #activator="{ props }">
        <v-chip v-bind="props" append-icon="mdi-clock-remove-outline">
          {{ getName(eligiblePerson.personId.personId) }}
        </v-chip>
      </template>
    </v-tooltip>
    <v-tooltip v-else text="This class is not currently available">
      <template #activator="{ props }">
        <v-chip v-bind="props" append-icon="mdi-clock">
          {{ getName(eligiblePerson.personId.personId) }}
        </v-chip>
      </template>
    </v-tooltip>

    <!-- Instructor Enrollment Section -->
    <v-chip
      v-if="
        eligiblePerson.instructorStatus ==
        ClassInstructorEligibilityStatus.ENROLLED
      "
      @click="
        updateInstructorEnrollment(false, eligiblePerson.personId.personId)
      "
      color="purple"
      append-icon="mdi-school-outline"
    >
      <v-btn variant="text" :loading="loading"
        >Unenroll {{ getName(eligiblePerson.personId.personId) }} as
        Instructor</v-btn
      >
    </v-chip>
    <v-chip
      v-else-if="
        eligiblePerson.instructorStatus ==
        ClassInstructorEligibilityStatus.ELIGIBLE
      "
      @click="
        updateInstructorEnrollment(true, eligiblePerson.personId.personId)
      "
      color="indigo"
      append-icon="mdi-school"
    >
      <v-btn variant="text" :loading="loading"
        >Enroll {{ getName(eligiblePerson.personId.personId) }} as
        Instructor</v-btn
      >
    </v-chip>
  </div>
</template>
<script setup lang="ts">
const { formatRawCost } = useUtility();

import { useUtility } from "@/composables/useUtility";
import type { CalendarClass } from "@/composables/classes/useClassManager";
import type { ClassAvailability } from "../../../cbi-generator/generated/dto/ClassAvailability";
import type { ClassEligibility } from "../../../cbi-generator/generated/dto/ClassEligibility";
import { ClassEligibilityStatus } from "../../../cbi-generator/generated/dto/ClassEligibilityStatus";
import { ClassInstructorEligibilityStatus } from "../../../cbi-generator/generated/dto/ClassInstructorEligibilityStatus";
import { ClassSignupAvailabilityStatus } from "../../../cbi-generator/generated/dto/ClassSignupAvailabilityStatus";
import { SignupTypeEnum as UnifiedSignupTypeEnum } from "../../../cbi-generator/generated/dto/SignupTypeEnum";

const { formatDateNY, formatTimeNY } = useUtility();

const getAvailableOnText = (availability: ClassAvailability) => {
  const slotAvailableText =
    (availability.availableSlots || 0) > 0
      ? " for " + availability.availableSlots + " signups"
      : "";
  return (
    "This class will be available on " +
    formatDateNY(availability.availableOn) +
    slotAvailableText
  );
};

const props = defineProps<{
  eligibility: ClassEligibility[];
  classInstance: CalendarClass;
  loading: boolean;
  updateEnrollment: (
    type: UnifiedSignupTypeEnum | undefined,
    personId: number,
  ) => Promise<void>;
  updateInstructorEnrollment: (
    isEnroll: boolean,
    personId: number,
  ) => Promise<void>;
  getName: (personId: number) => string | undefined;
}>();
</script>
